// Translations for the site's own text (menus, buttons, panels, glossary).
// News headlines stay in their original language (with a free "Translate" link).
//
// Language choice: the visitor's pick (saved in the browser), otherwise the
// browser's language if we support it, otherwise English.
// Country names come from the browser itself (Intl.DisplayNames), so all ~200
// countries are translated for free without us storing any lists.

import en from './en.js';
import mt from './mt.js';
import es from './es.js';
import de from './de.js';
import zh from './zh.js';
import ru from './ru.js';
import lt from './lt.js';
import it from './it.js';

export const LANGS = [
    { code: 'en', name: 'English' },
    { code: 'mt', name: 'Malti' },
    { code: 'it', name: 'Italiano' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh', name: '中文' },
    { code: 'ru', name: 'Русский' },
    { code: 'lt', name: 'Lietuvių' },
];
const DICTS = { en, mt, it, es, de, zh, ru, lt };
const KEY = 'nnw:lang';

function detect() {
    try {
        const saved = localStorage.getItem(KEY);
        if (saved && DICTS[saved]) return saved;
    } catch {
        /* ignore */
    }
    for (const l of navigator.languages || [navigator.language || 'en']) {
        const code = String(l).slice(0, 2).toLowerCase();
        if (DICTS[code]) return code;
    }
    return 'en';
}

export const lang = detect();
const dict = DICTS[lang];
document.documentElement.lang = lang;

/** Translate a key, filling {placeholders}. Falls back to English, then to the key. */
export function t(key, vars = {}) {
    const s = dict[key] ?? en[key] ?? key;
    return String(s).replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
}

/** "1 story" / "5 stories" in the current language. */
export function stories(n) {
    if (lang === 'ru' || lang === 'lt') {
        const key = pluralKey(n);
        return t(`story.${key}`, { n });
    }
    return t(n === 1 ? 'story.one' : 'story.other', { n });
}

// Russian/Lithuanian have one/few/many forms.
function pluralKey(n) {
    const r = new Intl.PluralRules(lang).select(n);
    return dict[`story.${r}`] ? r : n === 1 ? 'one' : 'other';
}

export function setLang(code) {
    if (!DICTS[code] || code === lang) return;
    try {
        localStorage.setItem(KEY, code);
    } catch {
        /* ignore */
    }
    location.reload(); // simplest way to redraw everything in the new language
}

let regionNames = null;
try {
    regionNames = new Intl.DisplayNames([lang, 'en'], { type: 'region' });
} catch {
    regionNames = null;
}

/** Country name in the current language (browser-provided), with the English name as fallback. */
export function countryName(iso2, fallback = '') {
    if (lang === 'en' || !iso2) return fallback || iso2;
    try {
        const n = regionNames?.of(String(iso2).toUpperCase());
        return n && n !== iso2 ? n : fallback;
    } catch {
        return fallback;
    }
}

/** Add strings at run time (the tool pages load their own dictionaries; see ./tools.js). */
export function extend(strings, english) {
    Object.assign(dict, strings);
    if (english && english !== strings) Object.assign(en, english);
}

/** Plural-aware translation: key.one / key.few / key.many / key.other (Intl plural rules), filling {n}. */
export function tn(key, n, vars = {}) {
    let form = 'other';
    try {
        form = new Intl.PluralRules(lang).select(n);
    } catch {
        form = n === 1 ? 'one' : 'other';
    }
    // Use this language's own form; a language without it uses its own "other" (never the English singular).
    const k = dict[`${key}.${form}`] ? `${key}.${form}` : `${key}.other`;
    return t(k, { n, ...vars });
}

/** Locale for numbers and dates in the current language. */
export const locale = { en: 'en-IE', mt: 'mt-MT', it: 'it-IT', es: 'es-ES', de: 'de-DE', zh: 'zh-CN', ru: 'ru-RU', lt: 'lt-LT' }[lang] || 'en-IE';

export const catName = (slug, fallback = '') => (dict[`cat.${slug}`] || en[`cat.${slug}`] || fallback);
export const contName = (name) => t(`cont.${name}`);

/** Glossary entry text {label, what, why} in the current language, or null to use English. */
export function glossaryText(id) {
    const g = dict.glossary?.[id];
    return g ? { label: g[0], what: g[1], why: g[2] } : null;
}

/** Fill static HTML: data-i18n (text), data-i18n-html (our own markup, e.g. links), data-i18n-ph (placeholder),
 *  data-i18n-aria (aria-label). */
export function applyStatic(root = document) {
    root.querySelectorAll('[data-i18n]').forEach((el) => (el.textContent = t(el.dataset.i18n)));
    root.querySelectorAll('[data-i18n-html]').forEach((el) => (el.innerHTML = t(el.dataset.i18nHtml)));
    root.querySelectorAll('[data-i18n-ph]').forEach((el) => el.setAttribute('placeholder', t(el.dataset.i18nPh)));
    root.querySelectorAll('[data-i18n-aria]').forEach((el) => el.setAttribute('aria-label', t(el.dataset.i18nAria)));
}

/** Language picker in the header. */
export function initLanguagePicker() {
    const header = document.querySelector('.header-inner');
    if (!header) return;
    const sel = document.createElement('select');
    sel.className = 'lang-select';
    sel.setAttribute('aria-label', t('lang.label'));
    sel.innerHTML = LANGS.map((l) => `<option value="${l.code}"${l.code === lang ? ' selected' : ''}>${l.name}</option>`).join('');
    sel.addEventListener('change', () => setLang(sel.value));
    // On phones the full picker is hidden behind a compact "IT ▾" label; tapping
    // it still opens the phone's own list with every language name.
    const wrap = document.createElement('label');
    wrap.className = 'lang-wrap';
    wrap.innerHTML = `<span class="lang-code" aria-hidden="true">${lang.toUpperCase()}</span>`;
    wrap.appendChild(sel);
    header.appendChild(wrap);
}
